import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Box, Center, HStack, Heading, Link, Progress, Spacer, Spinner, Text, VStack } from '@chakra-ui/react';
import { getTrackPathPrefix, useContentTrackContext } from '@src/components/sco/Education/ContentTrackContext';
import { ContentTrackDotIndicator } from '@src/components/sco/Education/CourseTrackNav/ContentTrackDotIndicator';
import { PaywalledAction } from '@src/components/sco/PaywalledAction';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ContentTrackSection } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import { usePathname } from 'next/navigation';
import React, { Fragment } from 'react';
type ContentTrackNavProps = {
  contentTrackSlug: string;
  contentTrackSectionSlug?: string;
  contentTrackStepSlug?: string;
};
export const ContentTrackNav = props => {
  const $ = _c(25);
  const {
    currentUser
  } = useCurrentUser();
  const pathname = usePathname();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ContentTrackNav.queries.contentTrack();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.contentTrackSlug) {
    t1 = {
      variables: {
        slug: props.contentTrackSlug
      }
    };
    $[1] = props.contentTrackSlug;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const query = useQuery(t0, t1);
  const contentTrack = query.data?.contentTrack;
  const trackContext = useContentTrackContext();
  let sectionPercents;
  let t2;
  if ($[3] !== trackContext.contentTrackEnrollment?.progress?.sectionStatuses) {
    sectionPercents = trackContext.contentTrackEnrollment?.progress?.sectionStatuses?.map(_temp) || [];
    t2 = sectionPercents.filter(_temp2);
    $[3] = trackContext.contentTrackEnrollment?.progress?.sectionStatuses;
    $[4] = sectionPercents;
    $[5] = t2;
  } else {
    sectionPercents = $[4];
    t2 = $[5];
  }
  const countSectionsComplete = t2.length;
  const countSections = sectionPercents.length || 0;
  const trackPercentComplete = trackContext.contentTrackEnrollment?.progress?.completionAsPercent || 0;
  let t3;
  if ($[6] !== query.loading) {
    t3 = query.loading && <Center padding="1em"><Spinner /></Center>;
    $[6] = query.loading;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== query.error) {
    t4 = query.error && <p>{"Error: "}{query.error.message}</p>;
    $[8] = query.error;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== contentTrack || $[11] !== countSections || $[12] !== countSectionsComplete || $[13] !== currentUser?.isRegistered || $[14] !== pathname || $[15] !== props.contentTrackSectionSlug || $[16] !== props.contentTrackSlug || $[17] !== props.contentTrackStepSlug || $[18] !== query.data || $[19] !== trackPercentComplete) {
    t5 = contentTrack?.sections && <Box height="full" overflowY="auto" paddingY={3} width="full" css={{
      "& &::-webkit-scrollbar": {
        width: "16px",
        borderRadius: "8px",
        backgroundColor: "rgba(0, 0, 0, 0.05)"
      },
      "& &::-webkit-scrollbar-thumb": {
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        borderRadius: "8px"
      },
      scrollbarWidth: "thin",
      scrollbarColor: "rgba(85, 84, 115, 1) rgba(0, 0, 0, 0.05)"
    }}><Box width="full"><VStack align="flex-start" bgColor="colorPalette.subtle" border="1px solid" borderColor="colorPalette.border" borderRadius="xl" boxShadow="base" gap={0} overflow="hidden" width="full"><VStack align="stretch" gap={2} padding={6} width="full"><NextLink href={`/${getTrackPathPrefix(contentTrack)}/${props.contentTrackSlug}`}><Heading lineHeight="shorter" size="lg">{contentTrack.title}</Heading></NextLink><Spacer /><PaywalledAction contentPermission="canAccessCourses" isUnlocked={currentUser?.isRegistered} variant="button-only" upgradeCtaButtonProps={{
              variant: "outline",
              size: "sm",
              href: `${pathname}?prompt=register`
            }}><VStack align="flex-start" gap={3} width="full"><Text color="colorPalette.fg" fontSize="xs" fontWeight="medium" lineHeight="shorter">{countSectionsComplete}{" of "}{countSections} lessons completed{" \u2022 "}{trackPercentComplete}%</Text><Progress.Root colorPalette="cyan" size="sm" value={Math.max(trackPercentComplete, 5)} variant="subtle" width="full"><Progress.Track><Progress.Range /></Progress.Track></Progress.Root></VStack></PaywalledAction></VStack></VStack></Box>{query.data?.contentTrack && query.data.contentTrack.sections.map((section, index, array) => {
        const sectionHasOneStep = section.steps.length === 1;
        const isSectionActive = section.slug === props.contentTrackSectionSlug;
        return <Fragment key={section.id}><Box bg="colorPalette.subtle" height="8px" marginLeft="1.27em" width="3px" /><VStack align="flex-start" bgColor={isSectionActive ? "colorPalette.focusRing" : "colorPalette.subtle/50"} borderRadius="lg" boxShadow={isSectionActive ? "md" : "none"} gap={0} paddingX={1.5} style={{
            display: "flex",
            flexDirection: "column"
          }} transition="all 0.2s ease-in" width="full" _hover={{
            bgColor: isSectionActive ? "colorPalette.solid" : "colorPalette.emphasized"
          }}><HStack align="stretch" gap={3} justify="flex-start" style={{
              flex: 1
            }} width="full"><VStack align="center" gap={0} style={{
                flex: 1
              }}><Box bg={isSectionActive ? "colorPalette.focusRing" : "colorPalette.subtle"} flex={1} width="3px" /><ContentTrackDotIndicator contentTrackSectionId={section.id} isActive={isSectionActive} /><Box bg={isSectionActive ? "colorPalette.focusRing" : "colorPalette.subtle"} flex={1} width="3px" /></VStack><Box paddingY={4} width="full"><NextLink passHref={true} href={sectionHasOneStep || true ? `/${getTrackPathPrefix(contentTrack)}/${contentTrack.slug}/${section.slug}/${section.steps[0]?.slug}` : `/${getTrackPathPrefix(contentTrack)}/${contentTrack.slug}/${section.slug}`}><Text _hover={{
                    color: "white"
                  }} as="span" color={isSectionActive ? "colorPalette.fg" : "colorPalette.fg/50"} display="block" fontSize="sm" fontWeight="medium" lineHeight="shorter" transition="color 0.2s ease-in">{section.title}</Text></NextLink></Box></HStack>{!sectionHasOneStep && isSectionActive && <VStack borderColor="colorPalette.fg/30" borderLeft="3px solid" gap={4} marginLeft="3.5" paddingBottom={6} paddingLeft="2" paddingTop={1}>{section.steps.map(step => {
                const isStepActive = step.slug === props.contentTrackStepSlug;
                return <HStack key={step.id} align="center" gap={2} justify="flex-start" paddingRight={4} width="full"><ContentTrackDotIndicator contentTrackSectionId={section.id} contentTrackStepId={step.id} isActive={isStepActive} size="xs" /><Box width="full"><NextLink passHref={true} href={`/${getTrackPathPrefix(contentTrack)}/${contentTrack.slug}/${section.slug}/${step.slug}`}><Link display="block"><Text color={isStepActive ? "colorPalette.fg" : "colorPalette.fg/50"} fontSize="xs" fontWeight="medium" lineHeight="shorter">{step.title}</Text></Link></NextLink></Box></HStack>;
              })}</VStack>}</VStack></Fragment>;
      })}</Box>;
    $[10] = contentTrack;
    $[11] = countSections;
    $[12] = countSectionsComplete;
    $[13] = currentUser?.isRegistered;
    $[14] = pathname;
    $[15] = props.contentTrackSectionSlug;
    $[16] = props.contentTrackSlug;
    $[17] = props.contentTrackStepSlug;
    $[18] = query.data;
    $[19] = trackPercentComplete;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== t3 || $[22] !== t4 || $[23] !== t5) {
    t6 = <VStack align="flex-start" gap={0} height="full" overflow="hidden" width="full">{t3}{t4}{t5}</VStack>;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  return t6;
};
ContentTrackNav.queries = {
  contentTrack: () => gql`
    query ($slug: String!) {
      contentTrack(where: { slug: $slug }) {
        id
        title
        slug
        type
        color {
          hex
        }
        sections {
          id
          title
          slug
          steps {
            id
            title
            slug
          }
        }
      }
    }
  `
};
function _temp(x) {
  return x.completionAsPercent;
}
function _temp2(x_0) {
  return x_0 >= 95;
}